import { pdfImageColumnsQuery, readPdfImageColumnsPreference, } from '@/lib/report/pdfImageColumns';
import { ApiError, apiFetch, apiRequest, apiUpload, getAccessToken, resolveApiBase } from './client';
const BASE = '/standard-reports';
// ============ CRUD ============
export async function getStandardReport(studyId) {
    try {
        return await apiFetch(`${BASE}?studyId=${encodeURIComponent(studyId)}`);
    }
    catch (e) {
        if (e instanceof ApiError && e.status === 404) {
            // Mock fallback: the echo-measurements mock writes a shadow report
            // (populated by "Save to report" in the echo panel) so the
            // StandardReportPage can surface cardiac measurements without a
            // real backend. Auto-fallback is dev-only — in production a 404
            // simply means "no report yet", and serving localStorage data there
            // would mask routing/outage incidents. The isMockForced() escape
            // hatch works in any build (the e2e suite runs a production bundle
            // and seeds the force flag), same gate as echo-measurements.ts.
            try {
                const mock = await import('./echo-measurements-mock');
                if (process.env.NODE_ENV === 'development' || mock.isMockForced()) {
                    const shadow = mock.readShadowReport?.(studyId);
                    if (shadow) {
                        console.info('[standard-reports] mock shadow report hit for study', studyId);
                        return shadow;
                    }
                }
            }
            catch {
                // no mock module — fall through to null
            }
            return null;
        }
        throw e;
    }
}
export async function createStandardReport(data) {
    return await apiRequest('POST', BASE, {
        studyId: data.studyId,
        templateId: data.templateId,
        status: data.status,
        content: data.content,
        images: data.images ?? [],
    });
}
export async function updateStandardReport(reportId, data) {
    return await apiRequest('PUT', `${BASE}/${reportId}`, {
        status: data.status,
        content: data.content,
        images: data.images,
        // Optimistic-concurrency token. `content` is replaced wholesale, so
        // without this a concurrent writer's keys (populate-echo's echoContext /
        // echoDerived) are silently dropped by whichever save lands last. Absent
        // => the server keeps its pre-existing last-write-wins behaviour.
        expectedUpdatedAt: data.expectedUpdatedAt,
        // undefined = leave unchanged; '' = clear the stored template.
        templateId: data.templateId,
    });
}
export async function finalizeStandardReport(reportId, stats) {
    return await apiRequest('POST', `${BASE}/${reportId}/finalize`, stats
        ? { dictated: stats.dictated, dictWords: stats.dictWords, refineUsed: stats.refineUsed }
        : undefined);
}
export async function addStandardReportAddendum(reportId, reason) {
    return await apiRequest('POST', `${BASE}/${reportId}/addendum`, { reason });
}
export async function unfinalizeStandardReport(reportId) {
    await apiRequest('POST', `${BASE}/${reportId}/unfinalize`);
}
/** Permanently deletes a never-signed draft (204). Signed reports are refused by the server. */
export async function deleteStandardReport(reportId) {
    await apiRequest('DELETE', `${BASE}/${reportId}`);
}
// ============ PDF ============
/**
 * Fetch the report PDF as a Blob (key images are embedded server-side).
 *
 * `imageColumns` is the key-image layout. When omitted the caller inherits
 * the signed-in user's `pdfImageColumns` preference from the shared cache
 * (see lib/report/pdfImageColumns.ts), so a worklist download renders the
 * same layout the editor previewed; when that too is unset the param is left
 * off and the server follows the report design.
 */
export async function getStandardReportPdfBlob(reportId, imageColumns) {
    const token = getAccessToken();
    const headers = {};
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const columns = imageColumns ?? readPdfImageColumnsPreference();
    const response = await fetch(`${resolveApiBase(BASE)}${BASE}/${reportId}/pdf${pdfImageColumnsQuery(columns)}`, {
        headers,
        credentials: 'include',
    });
    if (!response.ok) {
        throw new ApiError(response.status, await response.text().catch(() => 'PDF generation failed'));
    }
    return await response.blob();
}
/**
 * Render an in-editor PDF preview from the supplied state without
 * touching the persisted row. Returns a blob (so the caller can drive
 * the URL.createObjectURL lifecycle themselves).
 */
export async function getStandardReportPdfPreview(reportId, body) {
    const token = getAccessToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${resolveApiBase(BASE)}${BASE}/${reportId}/pdf-preview`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new ApiError(response.status, await response.text().catch(() => 'PDF preview failed'));
    }
    return await response.blob();
}
// ============ Images (GCS) ============
export async function uploadStandardReportImage(reportId, imageId, blob) {
    const formData = new FormData();
    formData.append('reportId', reportId);
    formData.append('imageId', imageId);
    formData.append('file', blob, `${imageId}.png`);
    const result = await apiUpload('POST', `${BASE}/images`, formData);
    return result.storagePath;
}
export function getStandardReportImageUrl(reportId, imageId) {
    return `${resolveApiBase(BASE)}${BASE}/images/${reportId}/${imageId}.png`;
}
// ============ Multi-report / Prior ============
export async function getStandardReportsByStudy(studyId) {
    return await apiFetch(`${BASE}/by-study?studyId=${encodeURIComponent(studyId)}`);
}
export async function getPriorStandardReports(patientId, 
/**
 * Anchor study for enterprise-group widening: with it, the backend also
 * surfaces same-MRN priors from sibling clinics in the anchor clinic's
 * enterprise group (membership-gated, exact-match only). Without it,
 * same-clinic legacy behavior.
 */
studyId) {
    const params = new URLSearchParams({ patientId });
    if (studyId)
        params.set('studyId', studyId);
    return await apiFetch(`${BASE}/prior?${params.toString()}`);
}
